import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Activity, AlertCircle, RefreshCw, Bot, Server, Trash2, XCircle,
  CheckCircle, Clock, Filter, ChevronDown, ChevronUp, FileText, Shield,
  Database, BarChart3
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';
import { useI18n } from '../../../i18n';

const AIAgentActions = () => {
  const { t } = useI18n();
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAction, setSelectedAction] = useState(null);
  const [filters, setFilters] = useState({
    action_type: '',
    category: '',
    success: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 15,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      const [actionsData, statsData] = await Promise.all([
        aiAgent.getActions(params),
        aiAgent.getActionsStats()
      ]);
      setActions(actionsData.items || []);
      setTotalPages(actionsData.total_pages || 1);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
      toast.error(t('aiAgent.loadActionsError'));
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Action type icon
  const getActionIcon = (type) => {
    const icons = {
      monitoring_cycle: Activity,
      anomaly_detected: AlertCircle,
      ai_analysis: Bot,
      auto_recovery: RefreshCw,
      alert_sent: Zap,
      service_restart: Server,
      cache_cleared: Trash2,
      scan_cancelled: XCircle,
      report_generated: FileText,
      health_check: Shield,
      resource_freed: Database,
      db_optimized: Database,
    };
    const Icon = icons[type] || Zap;
    return <Icon className="w-4 h-4" />;
  };

  // Category badge
  const getCategoryBadge = (category) => {
    const config = {
      monitoring: { color: 'bg-blue-500/20 text-blue-400', label: 'Monitoring' },
      analysis: { color: 'bg-purple-500/20 text-purple-400', label: 'Analyse' },
      recovery: { color: 'bg-orange-500/20 text-orange-400', label: 'Recovery' },
      alerting: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Alerte' },
      maintenance: { color: 'bg-gray-500/20 text-gray-400', label: 'Maintenance' },
      reporting: { color: 'bg-green-500/20 text-green-400', label: 'Rapport' },
    };
    const cfg = config[category] || config.monitoring;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
        {cfg.label}
      </span>
    );
  };

  // Action type label
  const getActionTypeLabel = (type) => {
    const labels = {
      monitoring_cycle: 'Cycle Monitoring',
      anomaly_detected: 'Anomalie Detectee',
      ai_analysis: 'Analyse IA',
      auto_recovery: 'Auto-Recovery',
      alert_sent: 'Alerte Envoyee',
      service_restart: 'Redemarrage Service',
      cache_cleared: 'Cache Vide',
      scan_cancelled: 'Scan Annule',
      report_generated: 'Rapport Genere',
      health_check: 'Health Check',
      resource_freed: 'Ressources Liberees',
      db_optimized: 'DB Optimisee',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            {t('aiAgent.actionsTitle')}
          </h3>
          <p className="text-gray-400 text-sm mt-1">{t('aiAgent.actionsSubtitle')}</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
            <p className="text-gray-500 text-sm">{t('aiAgent.totalActions')}</p>
            <p className="text-2xl font-bold text-white">{stats.total_actions || 0}</p>
          </div>
          <div className="bg-brand-black rounded-xl p-4 border border-green-500/30">
            <p className="text-gray-500 text-sm">{t('aiAgent.successful')}</p>
            <p className="text-2xl font-bold text-green-400">{stats.successful_actions || 0}</p>
          </div>
          <div className="bg-brand-black rounded-xl p-4 border border-red-500/30">
            <p className="text-gray-500 text-sm">{t('aiAgent.failed')}</p>
            <p className="text-2xl font-bold text-red-400">{stats.failed_actions || 0}</p>
          </div>
          <div className="bg-brand-black rounded-xl p-4 border border-blue-500/30">
            <p className="text-gray-500 text-sm">{t('aiAgent.successRate')}</p>
            <p className="text-2xl font-bold text-blue-400">{(stats.success_rate || 0).toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Stats by Type */}
      {stats?.actions_by_type && Object.keys(stats.actions_by_type).length > 0 && (
        <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-brand-green" />
            {t('aiAgent.actionsByType')}
          </h4>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.actions_by_type).map(([type, count]) => (
              <div key={type} className="flex items-center gap-2 px-3 py-1.5 bg-brand-black-lighter rounded-lg">
                {getActionIcon(type)}
                <span className="text-gray-400 text-sm">{getActionTypeLabel(type)}:</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 p-4 bg-brand-black rounded-xl border border-gray-800">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterType')}</label>
          <select
            value={filters.action_type}
            onChange={(e) => setFilters(f => ({ ...f, action_type: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.all')}</option>
            <option value="monitoring_cycle">Monitoring</option>
            <option value="anomaly_detected">Anomalie</option>
            <option value="ai_analysis">Analyse IA</option>
            <option value="auto_recovery">Recovery</option>
            <option value="alert_sent">Alerte</option>
            <option value="service_restart">Redemarrage</option>
            <option value="health_check">Health Check</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterCategory')}</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.allCategories')}</option>
            <option value="monitoring">Monitoring</option>
            <option value="analysis">Analyse</option>
            <option value="recovery">Recovery</option>
            <option value="alerting">Alerte</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterResult')}</label>
          <select
            value={filters.success}
            onChange={(e) => setFilters(f => ({ ...f, success: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.all')}</option>
            <option value="true">{t('aiAgent.succeeded')}</option>
            <option value="false">{t('aiAgent.failed')}</option>
          </select>
        </div>
      </div>

      {/* Actions List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
        </div>
      ) : actions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('aiAgent.noActionsRecorded')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((action) => (
            <div
              key={action.id}
              className={`bg-brand-black rounded-xl border transition-all ${
                selectedAction?.id === action.id
                  ? 'border-brand-green/50'
                  : 'border-gray-800 hover:border-gray-700'
              }`}
            >
              {/* Action Header */}
              <div
                className="p-3 cursor-pointer"
                onClick={() => setSelectedAction(
                  selectedAction?.id === action.id ? null : action
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${action.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {getActionIcon(action.action_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">
                          {getActionTypeLabel(action.action_type)}
                        </span>
                        {getCategoryBadge(action.category)}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(action.created_at).toLocaleString()}
                        </span>
                        {action.duration_ms && (
                          <span>{action.duration_ms}ms</span>
                        )}
                        {action.ai_model_used && (
                          <span className="flex items-center gap-1 text-purple-400">
                            <Bot className="w-3 h-3" />
                            {action.ai_model_used}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {action.success ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    {selectedAction?.id === action.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Action Details */}
              {selectedAction?.id === action.id && (
                <div className="px-3 pb-3 border-t border-gray-800 mt-2 pt-3">
                  {action.description && (
                    <p className="text-gray-400 text-sm mb-3">{action.description}</p>
                  )}
                  {action.error_message && (
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg mb-3">
                      <p className="text-red-400 text-sm">{action.error_message}</p>
                    </div>
                  )}
                  {action.details && Object.keys(action.details).length > 0 && (
                    <div className="bg-brand-black-lighter rounded-lg p-3">
                      <p className="text-gray-500 text-xs mb-2">Details:</p>
                      <pre className="text-gray-300 text-xs overflow-x-auto">
                        {JSON.stringify(action.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-brand-black-lighter text-gray-400 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-400">
            Page {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-brand-black-lighter text-gray-400 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AIAgentActions;
